import { supabase } from '../supabase';

export const PROFILE_PHOTO_BUCKET = 'profile-photos';
const MAX_FILE_SIZE = 2 * 1024 * 1024; // 2MB
const ALLOWED_MIME_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

const isExternalUrl = (value?: string | null): boolean =>
  !!value && (value.startsWith('http://') || value.startsWith('https://'));

const buildStoragePath = (userId: string, fileName: string) => {
  const extension = fileName.split('.').pop()?.toLowerCase() || 'jpg';
  const uniqueSuffix = `${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
  return `${userId}/${uniqueSuffix}.${extension}`;
};

interface ServiceResult<T = undefined> {
  success: boolean;
  error?: string;
  data?: T;
}

const validateFile = (file: File): ServiceResult => {
  if (!ALLOWED_MIME_TYPES.includes(file.type)) {
    return {
      success: false,
      error: 'Unsupported file format. Please upload JPG, PNG, or WebP images.'
    };
  }

  if (file.size > MAX_FILE_SIZE) {
    return {
      success: false,
      error: 'File size must be less than 2MB.'
    };
  }

  return { success: true };
};

export const profilePhotoService = {
  async upload(
    userId: string,
    file: File,
    existingPath?: string | null
  ): Promise<ServiceResult<{ path: string; signedUrl: string | null }>> {
    const validation = validateFile(file);
    if (!validation.success) {
      return { success: false, error: validation.error };
    }

    const storagePath = buildStoragePath(userId, file.name);
    const { error: uploadError } = await supabase.storage
      .from(PROFILE_PHOTO_BUCKET)
      .upload(storagePath, file, {
        cacheControl: '3600',
        upsert: false,
        contentType: file.type
      });

    if (uploadError) {
      console.error('Supabase storage upload error:', uploadError);
      return { success: false, error: uploadError.message };
    }

    if (existingPath && !isExternalUrl(existingPath)) {
      await supabase.storage.from(PROFILE_PHOTO_BUCKET).remove([existingPath]).catch(() => undefined);
    }

    const { error: updateError } = await supabase
      .from('profiles')
      .update({
        profile_photo: storagePath,
        updated_at: new Date().toISOString()
      })
      .eq('user_id', userId);

    if (updateError) {
      console.error('❌ Supabase profiles table update error:', {
        message: updateError.message,
        details: updateError.details,
        hint: updateError.hint,
        code: updateError.code
      });
      return { success: false, error: `Database error: ${updateError.message}` };
    }

    const signedUrl = await profilePhotoService.getSignedUrl(storagePath);

    return { success: true, data: { path: storagePath, signedUrl } };
  },

  async remove(userId: string, existingPath?: string | null): Promise<ServiceResult> {
    if (existingPath && !isExternalUrl(existingPath)) {
      const { error: removeError } = await supabase.storage
        .from(PROFILE_PHOTO_BUCKET)
        .remove([existingPath]);

      if (removeError) {
        return { success: false, error: removeError.message };
      }
    }

    const { error: updateError } = await supabase
      .from('profiles')
      .update({
        profile_photo: null,
        updated_at: new Date().toISOString()
      })
      .eq('user_id', userId);

    if (updateError) {
      console.error('❌ Supabase profiles table update error (remove):', {
        message: updateError.message,
        details: updateError.details,
        hint: updateError.hint,
        code: updateError.code
      });
      return { success: false, error: `Database error: ${updateError.message}` };
    }

    return { success: true };
  },

  async getSignedUrl(path?: string | null, expiresInSeconds = 3600): Promise<string | null> {
    if (!path) return null;
    
    // Sanitize: If the path is a URL pointing to localhost:23333 (common dev artifact), ignore it
    if (path.includes('localhost:23333')) {
      console.warn('Ignored invalid localhost:23333 profile photo path:', path);
      return null;
    }

    if (isExternalUrl(path)) return path;

    const { data, error } = await supabase.storage
      .from(PROFILE_PHOTO_BUCKET)
      .createSignedUrl(path, expiresInSeconds);

    if (error) {
      console.error('Error generating signed profile photo URL:', error);
      return null;
    }

    // Double check the generated URL
    if (data?.signedUrl && data.signedUrl.includes('localhost:23333')) {
       console.warn('Generated signed URL points to localhost:23333, ignoring:', data.signedUrl);
       return null;
    }

    return data?.signedUrl ?? null;
  }
};

